// client/src/components/indicadores/AniversariantesReport.js
import React, { useState } from 'react';
import useAlert from '../../hooks/useAlert';
import { useAniversariantesReport } from '../../hooks/useReports';
import PageHeader from '../layout/PageHeader';
import { Cake, Filter as FilterIcon } from 'lucide-react';
import ReportTable from './ReportTable';
import { formatInTimeZone } from 'date-fns-tz';

const AniversariantesReport = () => {
    const { setAlert } = useAlert();
    
    // 🔥 ESTADO DE FILTROS
    const [filters, setFilters] = useState({
        month: new Date().getMonth() + 1,
        day: new Date().getDate()
    });

    const [showFilters, setShowFilters] = useState(true);

    // 🔥 USANDO NOVO HOOK - useAniversariantesReport
    const { 
        data: reportData = [], 
        isLoading,
        error 
    } = useAniversariantesReport(filters);

    // ✅ TRATAMENTO DE ERRO PADRONIZADO
    React.useEffect(() => {
        if (error) {
            console.error('❌ Erro no relatório de aniversariantes:', error);
            
            let errorMessage = 'Ocorreu um erro ao buscar o relatório de aniversariantes.';
            
            if (error.message.includes('Servidor indisponível')) {
                errorMessage = error.message;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            setAlert(errorMessage, 'danger');
        }
    }, [error, setAlert]);

    // 🔥 CONFIGURAÇÃO DAS COLUNAS DA TABELA
    const columns = [
        { 
            key: 'nome', 
            header: 'Nome', 
            render: (row) => row.nome || 'Nome não informado' 
        },
        { 
            key: 'dataNascimento', 
            header: 'Data de Nascimento', 
            render: (row) => row.dataNascimento 
                ? formatInTimeZone(row.dataNascimento, 'UTC', 'dd/MM/yyyy') 
                : 'N/D' 
        },
        { 
            key: 'email', 
            header: 'Email', 
            render: (row) => row.email || 'N/D' 
        },
        { 
            key: 'telefone', 
            header: 'Telefone', 
            render: (row) => row.telefone || 'N/D' 
        },
    ];

    // 🔥 OPÇÕES PARA SELECTS
    const meses = [
        "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", 
        "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
    ];
    
    const dias = Array.from({ length: 31 }, (_, i) => i + 1);

    // ✅ VERIFICAÇÃO DE CONEXÃO COM BACKEND
    const isBackendUnavailable = error?.message?.includes('Servidor indisponível');

    const pageActions = (
        <button 
            onClick={() => setShowFilters(prevState => !prevState)} 
            className="action-button action-button-secondary"
        >
            <FilterIcon size={18} className="icon"/> 
            {showFilters ? 'Ocultar Filtro' : 'Exibir Filtro'}
        </button>
    );

    // 🔥 TÍTULO DINÂMICO
    const getReportTitle = () => {
        const mesNome = meses[filters.month - 1];
        if (filters.day) {
            return `Aniversariantes do dia ${filters.day} de ${mesNome}`;
        }
        return `Aniversariantes de ${mesNome}`;
    };

    return (
        <div className="page-management-container report-page">
            <PageHeader
                icon={<Cake size={24} />}
                title="Relatório de Aniversariantes"
                actions={pageActions}
            />
            
            {/* 🔥 MENSAGEM DE ERRO DE CONEXÃO - PRIORIDADE MÁXIMA */}
            {isBackendUnavailable && (
                <div className="content-panel mb-4">
                    <div className="alert alert-danger">
                        <div className="d-flex align-items-center">
                            <div className="flex-grow-1">
                                <h5>❌ Erro de Conexão com o Servidor</h5>
                                <p className="mb-2">{error.message}</p>
                                <small className="text-muted">
                                    <strong>Para resolver:</strong><br/>
                                    1. Verifique se o servidor backend está rodando<br/>
                                    2. Confirme se a API está respondendo<br/>
                                    3. Verifique o comando: <code>npm run dev</code> no backend<br/>
                                    4. Recarregue a página após iniciar o servidor
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {!isBackendUnavailable && showFilters && (
                <div className="content-panel">
                    <h3 className="panel-title">Filtro</h3>
                    <div className="form-grid-container">
                        <div className="form-group">
                            <label htmlFor="month-select">Selecione o Mês</label>
                            <select 
                                id="month-select" 
                                className="form-input" 
                                value={filters.month} 
                                onChange={(e) => setFilters({ ...filters, month: parseInt(e.target.value) })}
                                disabled={isLoading}
                            >
                                {meses.map((m, i) => (
                                    <option key={i+1} value={i+1}>{m}</option>
                                ))}
                            </select>
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="day-select">Selecione o Dia (Opcional)</label>
                            <select 
                                id="day-select" 
                                className="form-input" 
                                value={filters.day} 
                                onChange={(e) => setFilters({ 
                                    ...filters, 
                                    day: e.target.value ? parseInt(e.target.value) : '' 
                                })}
                                disabled={isLoading}
                            >
                                <option value="">Todos os dias</option>
                                {dias.map(d => (
                                    <option key={d} value={d}>{d}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    
                    {/* 🔥 RESUMO DO FILTRO ATUAL */}
                    <div className="filter-summary" style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '0.375rem' }}>
                        <small className="text-muted">
                            <strong>Filtro atual:</strong> {meses[filters.month - 1]}
                            {filters.day && `, dia ${filters.day}`}
                        </small>
                    </div>
                </div>
            )}
            
            {!isBackendUnavailable && (
                <div className="table-container" style={{ marginTop: '1.5rem' }}>
                    <ReportTable 
                        title={getReportTitle()} 
                        columns={columns} 
                        data={reportData} 
                        isLoading={isLoading} 
                        emptyMessage={
                            filters.day 
                                ? `Nenhum aniversariante encontrado para o dia ${filters.day} de ${meses[filters.month - 1]}`
                                : `Nenhum aniversariante encontrado para ${meses[filters.month - 1]}`
                        }
                    />
                    
                    {/* 🔥 ESTATÍSTICAS */}
                    {reportData && reportData.length > 0 && (
                        <div className="content-panel" style={{ marginTop: '1rem' }}>
                            <div className="row">
                                <div className="col-md-4">
                                    <div className="stat-card">
                                        <h6>Total de Aniversariantes</h6>
                                        <p className="stat-value">{reportData.length}</p>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="stat-card">
                                        <h6>Com Email</h6>
                                        <p className="stat-value">
                                            {reportData.filter(item => item.email).length}
                                        </p>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="stat-card">
                                        <h6>Com Telefone</h6>
                                        <p className="stat-value">
                                            {reportData.filter(item => item.telefone).length}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AniversariantesReport;